import { lazy, type LazyExoticComponent, type ComponentType } from 'react';
import JsonTool from './pages/JsonTool';
import YamlTool from './pages/YamlTool';
import XmlTool from './pages/XmlTool';
import SqlTool from './pages/SqlTool';
import CodecTool from './pages/CodecTool';
import UnixTimeTool from './pages/UnixTimeTool';
import JwtDecodeTool from './pages/JwtDecodeTool';
import TokenTool from './pages/TokenTool';
import HmacTool from './pages/HmacTool';
import NumberBaseTool from './pages/NumberBaseTool';
import TextStatsTool from './pages/TextStatsTool';
import UrlParseTool from './pages/UrlParseTool';
import HttpStatusTool from './pages/HttpStatusTool';
import IpSubnetTool from './pages/IpSubnetTool';
import UuidTool from './pages/UuidTool';
import PasswordTool from './pages/PasswordTool';
import NanoIdTool from './pages/NanoIdTool';
import RegexTool from './pages/RegexTool';
import HashTool from './pages/HashTool';
import LoremTool from './pages/LoremTool';
import TextCaseTool from './pages/TextCaseTool';
import SortLinesTool from './pages/SortLinesTool';
import DedupeLinesTool from './pages/DedupeLinesTool';
import UnicodeTool from './pages/UnicodeTool';
import GzipTool from './pages/GzipTool';
import BinaryTool from './pages/BinaryTool';
import UnitTool from './pages/UnitTool';
import EmailTool from './pages/EmailTool';
import UrlValidateTool from './pages/UrlValidateTool';
import IpValidateTool from './pages/IpValidateTool';
import AesTool from './pages/AesTool';
import RsaKeygenTool from './pages/RsaKeygenTool';
import RsaTool from './pages/RsaTool';
import CurlConvertTool from './pages/CurlConvertTool';
import Base64FileTool from './pages/Base64FileTool';
/**
 * FakeData pulls in @faker-js/faker (zh_CN + en locales, ~190 KB
 * gzip). Eager-importing it would bloat the SPA shell for users who
 * never visit the page. Lazy import keeps the playground snappy and
 * parks faker in a dedicated chunk.
 */
const FakeDataTool = lazy(() => import('./pages/FakeDataTool'));
/**
 * ImageOptimize pulls in jSquash WASM codecs (mozjpeg / oxipng /
 * libwebp / avif / resize). They live in their own Vite manualChunks
 * (see vite.config.ts) so this lazy-import only ships the small
 * page-level glue + chip UI on first navigation; the actual encoder
 * WASM binaries are fetched on demand by the codec worker.
 */
const ImageOptimizeTool = lazy(() => import('./pages/ImageOptimizeTool'));
/**
 * ImageBatch shares the codec pool + jSquash chunks with image-optimize
 * (Vite dedupes the dynamic-imported chunks across the two pages). The
 * page-specific surface is just the queue list + fflate ZIP packer.
 */
const ImageBatchTool = lazy(() => import('./pages/ImageBatchTool'));
/**
 * CropTool shares the codec pool with image-optimize and image-batch.
 * The page-only surface is `ImageCropper` + the rect-state machine.
 */
const CropTool = lazy(() => import('./pages/CropTool'));
/**
 * ExifViewerTool pulls in `exifr` (~10 KB lite parser) and `piexifjs`
 * (~30 KB strip/inject). Both are dynamic-imported inside the lib;
 * the page itself stays small. Nothing in this chunk overlaps the
 * jSquash codec graph — exif-viewer never decodes pixels.
 */
const ExifViewerTool = lazy(() => import('./pages/ExifViewerTool'));
/**
 * QrTool dynamic-imports `qrcode` (~16 KB gzip). The package + its
 * dijkstrajs / pngjs runtime helpers live in the dedicated `qrcode`
 * manualChunk (see vite.config.ts) so the SPA shell pays nothing
 * unless the user actually opens this page.
 */
const QrTool = lazy(() => import('./pages/QrTool'));
/**
 * JsonPathTool dynamic-imports `jsonpath-plus` (~10 KB gzip + jsep).
 * The package lives in its own `jsonpath` manualChunk so the SPA shell
 * never pays for the JSONPath compiler unless the page is open.
 */
const JsonPathTool = lazy(() => import('./pages/JsonPathTool'));
/**
 * JsonSchemaTool dynamic-imports `ajv` + `ajv-formats` (~50 KB raw, ~18
 * KB gzip combined). Both packages live in the dedicated `ajv`
 * manualChunk so the SPA shell pays nothing.
 */
const JsonSchemaTool = lazy(() => import('./pages/JsonSchemaTool'));
/**
 * CronTool dynamic-imports `cronstrue` (~10 KB) + `cron-parser`
 * (~12 KB + luxon dependency). Pinned to the dedicated `cron` chunk
 * via vite manualChunks so neither lib hits the SPA shell.
 */
const CronTool = lazy(() => import('./pages/CronTool'));
/**
 * JsonYamlTool reuses the existing `yaml.ts` helper graph (already in
 * the SPA via the YAML formatter), so no new dep — Monaco is the
 * dominant cost. Lazy anyway so the SPA shell loads instantly.
 */
const JsonYamlTool = lazy(() => import('./pages/JsonYamlTool'));
/**
 * ColorTool dynamic-imports `culori` (~12 KB gzip after tree-shake).
 * Pinned to the dedicated `culori` chunk via vite manualChunks.
 */
const ColorTool = lazy(() => import('./pages/ColorTool'));
/**
 * TextDiffTool dynamic-imports `diff` (jsdiff, ~10 KB gzip). Pinned to
 * the dedicated `jsdiff` chunk via vite manualChunks.
 */
const TextDiffTool = lazy(() => import('./pages/TextDiffTool'));
/**
 * HtmlEntitiesTool dynamic-imports `he` (~5 KB gzip). Pinned to its
 * own `he` chunk via vite manualChunks so the SPA shell stays free
 * of the named-entity table for users who never open this page.
 */
const HtmlEntitiesTool = lazy(() => import('./pages/HtmlEntitiesTool'));
/**
 * HashFileTool dynamic-imports `js-md5` (~5 KB gzip) on demand —
 * SubtleCrypto doesn't expose MD5, so it's the only piece of code we
 * ship for it. SHA-1/256/512 use the browser's WebCrypto so they are
 * free. Pinned to a `js-md5` chunk via vite manualChunks.
 */
const HashFileTool = lazy(() => import('./pages/HashFileTool'));
/**
 * UserAgentTool dynamic-imports `ua-parser-js` (~10 KB gzip).
 * Pinned to its own `ua-parser` chunk via vite manualChunks.
 */
const UserAgentTool = lazy(() => import('./pages/UserAgentTool'));
/**
 * BcryptTool dynamic-imports `bcryptjs` (~10 KB gzip). Pinned to the
 * dedicated `bcryptjs` chunk via vite manualChunks so the SPA shell
 * never pays the cost.
 */
const BcryptTool = lazy(() => import('./pages/BcryptTool'));
/**
 * JwtSignTool reuses the existing `jwt.ts` helpers and `rsa.ts`
 * (already loaded by /tools/crypto/rsa-keygen). Lazy-load the page
 * itself so the SPA shell stays slim.
 */
const JwtSignTool = lazy(() => import('./pages/JwtSignTool'));
/**
 * CronBuilderTool shares the existing `cron` chunk (cronstrue +
 * cron-parser + luxon) with /tools/validate/cron, so loading this
 * page costs only its own glue once the cron chunk is on disk.
 */
const CronBuilderTool = lazy(() => import('./pages/CronBuilderTool'));
/**
 * SlugTool dynamic-imports `pinyin-pro` (~30 KB gzip) only when the
 * user actually feeds CJK input AND keeps `cjkMode='transliterate'`.
 * Pinned to a dedicated `pinyin-pro` chunk via vite manualChunks.
 */
const SlugTool = lazy(() => import('./pages/SlugTool'));
/**
 * ColorPaletteTool reuses the existing `culori` chunk (already loaded
 * by /tools/convert/color), so the page itself is just glue.
 */
const ColorPaletteTool = lazy(() => import('./pages/ColorPaletteTool'));
/**
 * QrReaderTool dynamic-imports `jsqr` (~10 KB gzip). Pinned to its
 * own `jsqr` chunk via vite manualChunks so the SPA shell stays
 * free of the QR decoder.
 */
const QrReaderTool = lazy(() => import('./pages/QrReaderTool'));
/**
 * JsonCsvTool dynamic-imports `papaparse` (~15 KB gzip). Pinned to a
 * dedicated `papaparse` chunk via vite manualChunks.
 */
const JsonCsvTool = lazy(() => import('./pages/JsonCsvTool'));
/**
 * PhoneTool dynamic-imports `libphonenumber-js/min` (~80 KB gzip:
 * country regex tables only, no carrier / timezone metadata).
 * Pinned to its own `libphonenumber-js` chunk via vite manualChunks.
 */
const PhoneTool = lazy(() => import('./pages/PhoneTool'));
/**
 * ColorExtractTool is 0-dep — pure JS k-means + canvas. Lazy-loaded
 * so the SPA shell never carries the algorithm for users who don't
 * open the page.
 */
const ColorExtractTool = lazy(() => import('./pages/ColorExtractTool'));
/**
 * MarkdownTool dynamic-imports the unified ecosystem
 * (remark + rehype + KaTeX HTML output). Pinned to `unified`
 * + `katex` chunks via vite manualChunks; reused by the
 * markdown ↔ html convert tool so the chunk graph dedupes.
 */
const MarkdownTool = lazy(() => import('./pages/MarkdownTool'));
/**
 * HtmlFormatTool / CssFormatTool dynamic-import `prettier/standalone`
 * + the corresponding parser plugin. Pinned to a single `prettier`
 * chunk via vite manualChunks so opening either tool warms the
 * shared chunk for the other.
 */
const HtmlFormatTool = lazy(() => import('./pages/HtmlFormatTool'));
const CssFormatTool = lazy(() => import('./pages/CssFormatTool'));
/**
 * SvgFormatTool dynamic-imports `svgo/browser` (~80 KB gzip), pinned
 * to a dedicated `svgo` chunk via vite manualChunks.
 */
const SvgFormatTool = lazy(() => import('./pages/SvgFormatTool'));
/**
 * JsonTomlTool dynamic-imports `smol-toml` (~5 KB gzip), pinned to
 * a dedicated `smol-toml` chunk via vite manualChunks.
 */
const JsonTomlTool = lazy(() => import('./pages/JsonTomlTool'));
/**
 * JsonXmlTool reuses the existing `fast-xml-parser` chunk (already
 * loaded by /tools/format/xml). Lazy-loaded so the page glue itself
 * stays out of the SPA shell.
 */
const JsonXmlTool = lazy(() => import('./pages/JsonXmlTool'));
/**
 * MarkdownHtmlTool reuses the same `unified` chunk as
 * /tools/format/markdown. The chunk graph dedupes naturally.
 */
const MarkdownHtmlTool = lazy(() => import('./pages/MarkdownHtmlTool'));
/**
 * MockJsonTool dynamic-imports `json-schema-faker` (~80 KB gzip).
 * Pinned to its own `json-schema-faker` chunk via vite manualChunks.
 */
const MockJsonTool = lazy(() => import('./pages/MockJsonTool'));
/**
 * Ipv6ToolsTool is 0-dep — pure JS parser and generator. Lazy-loaded
 * so the SPA shell never carries the parser.
 */
const Ipv6ToolsTool = lazy(() => import('./pages/Ipv6ToolsTool'));
/**
 * CookieDecoderTool is 0-dep. Lazy-loaded for consistency.
 */
const CookieDecoderTool = lazy(() => import('./pages/CookieDecoderTool'));
/**
 * WebSocketTool uses the browser-native WebSocket API. Lazy-loaded
 * so the SPA shell never carries the timeline UI.
 */
const WebSocketTool = lazy(() => import('./pages/WebSocketTool'));
/**
 * MacLookupTool uses a curated OUI table that's dynamic-imported
 * by the lib (`./macOuiTable`) — pinned to its own `mac-oui` chunk
 * via vite manualChunks so the table only ships when the user
 * opens the page.
 */
const MacLookupTool = lazy(() => import('./pages/MacLookupTool'));
/**
 * PunycodeTool — 0 dep RFC 3492 implementation lives in the same
 * lazy chunk as the page itself; nothing is pulled in at SPA boot.
 */
const PunycodeTool = lazy(() => import('./pages/PunycodeTool'));
/**
 * PlaceholderSvgTool — 0 dep SVG string assembly, no extra chunk
 * needed beyond the page itself.
 */
const PlaceholderSvgTool = lazy(() => import('./pages/PlaceholderSvgTool'));
/**
 * Wave 5 helper-backed network tools. The pages are thin React shells;
 * all business logic + DNS / WHOIS / fetch + SSRF lives on the helper
 * side under src/server/routes/net{Dns,Whois,Og}.ts. Each page is
 * lazy-loaded so the SPA shell stays small for users who never open
 * them.
 */
const DnsTool = lazy(() => import('./pages/DnsTool'));
const WhoisTool = lazy(() => import('./pages/WhoisTool'));
const OgPreviewTool = lazy(() => import('./pages/OgPreviewTool'));
/**
 * HttpTool is 0-dep (uses native fetch + the local HeaderTable
 * component). Lazy-loaded so the SPA shell stays small for users
 * who never open it.
 */
const HttpTool = lazy(() => import('./pages/HttpTool'));
/**
 * PDF tools (image-to-pdf / pdf-merge / pdf-split / pdf-watermark /
 * pdf-rotate) dynamic-import `pdf-lib` (~200 KB gzip) and `jspdf`
 * (~200 KB gzip, image-to-pdf only). Each is pinned to its own
 * `pdf-lib` / `jspdf` manualChunk via vite.config.ts so the SPA shell
 * never carries the PDF stack — the chunks only ship once a user
 * opens any of these five pages.
 */
const ImageToPdfTool = lazy(() => import('./pages/ImageToPdfTool'));
const PdfMergeTool = lazy(() => import('./pages/PdfMergeTool'));
const PdfSplitTool = lazy(() => import('./pages/PdfSplitTool'));
const PdfWatermarkTool = lazy(() => import('./pages/PdfWatermarkTool'));
const PdfRotateTool = lazy(() => import('./pages/PdfRotateTool'));
import {
  ArrowDownAZ,
  ArrowLeftRight,
  AtSign,
  Binary,
  BookText,
  Braces,
  Brackets,
  Cable,
  Calendar,
  Code,
  Combine,
  Cookie,
  Cpu,
  Crop,
  Database,
  Eye,
  FileArchive,
  FileCode,
  FileCode2,
  FileCog,
  FileDigit,
  FileImage,
  FileKey,
  FileKey2,
  FileText,
  FileType,
  Fingerprint,
  FlaskConical,
  Globe,
  Globe2,
  Hash,
  ImageIcon,
  Images,
  KeyRound,
  KeySquare,
  Key,
  Languages,
  Link,
  Link2,
  ListFilter,
  Lock,
  Mail,
  Network,
  Paintbrush,
  PenLine,
  PenTool,
  Phone,
  Pipette,
  Frame,
  Regex,
  Rocket,
  RotateCw,
  Scale,
  ScanLine,
  Scissors,
  ScrollText,
  Server,
  ServerCog,
  Sheet,
  Shield,
  Pilcrow,
  QrCode,
  Search,
  ListChecks,
  CalendarClock,
  CaseSensitive,
  Repeat,
  Palette,
  Diff,
  FileUp,
  Code2,
  FileLock2,
  Smartphone,
  ShieldCheck,
  Sparkles,
  Stamp,
  SwatchBook,
  Tag,
  Terminal,
  Timer,
  UserSearch,
  Users,
  Wand,
  Wifi,
  Zap,
  type LucideIcon,
} from 'lucide-react';

/**
 * Single source of truth for the general DevTools playground.
 *
 * Adding a new tool is a 3-step SOP:
 *   1. Append a `ToolDef` entry below;
 *   2. Create the React page under `web/src/tools/pages/<Name>Tool.tsx`;
 *   3. (Optional) Add tests under `tests/web/<id>Tool.test.ts`.
 *
 * Routes (`/tools/:category/:toolId`), the toolbox overview, the
 * category aggregation page and the contextual sidebar are all
 * driven from this file — DO NOT hard-code tool routes anywhere
 * else.
 *
 * APK-related tooling is intentionally NOT in this registry. Those
 * tools live under `/static-analysis/*` because they belong to a
 * different domain (APK file analysis, not generic developer tools)
 * and would otherwise force this schema to support non-`/tools`
 * prefixes.
 *
 * R2 reminder: tool input/output is working buffer, not preference.
 * Do not persist anything to localStorage from these pages.
 */

export type ToolCategoryId =
  | 'format'
  | 'encode'
  | 'crypto'
  | 'generate'
  | 'validate'
  | 'convert'
  | 'network'
  | 'image';

export interface CategoryDef {
  id: ToolCategoryId;
  /** Localized title, e.g. '格式化 · Formatters'. */
  label: string;
  /** Short label for the contextual sidebar, e.g. '格式化'. */
  shortLabel: string;
  icon: LucideIcon;
  /** One-line description, used by the overview page card. */
  description: string;
}

export const categories: CategoryDef[] = [
  {
    id: 'format',
    label: '格式化 · Formatters',
    shortLabel: '格式化',
    icon: FileCode,
    description: 'JSON / YAML / XML / SQL / HTML / Markdown 美化与压缩',
  },
  {
    id: 'encode',
    label: '编解码 · Encode/Decode',
    shortLabel: '编解码',
    icon: Code,
    description: 'Base64 / URL / HTML 实体 / Hex / Unicode（不含密钥操作）',
  },
  {
    id: 'crypto',
    label: '加密哈希 · Crypto & Hash',
    shortLabel: '加密哈希',
    icon: Lock,
    description: 'MD5 / SHA / HMAC / AES / RSA / JWT decode',
  },
  {
    id: 'generate',
    label: '生成器 · Generators',
    shortLabel: '生成器',
    icon: Sparkles,
    description: 'UUID / 随机密码 / Lorem / QR / Cron 表达式',
  },
  {
    id: 'validate',
    label: '校验器 · Validators',
    shortLabel: '校验器',
    icon: ShieldCheck,
    description: 'Regex / JSONPath / JSON Schema / Cron 预览',
  },
  {
    id: 'convert',
    label: '转换器 · Converters',
    shortLabel: '转换器',
    icon: ArrowLeftRight,
    description: '进制 / 时间戳 / 颜色 / JSON↔YAML / 单位',
  },
  {
    id: 'network',
    label: '网络 · Network',
    shortLabel: '网络',
    icon: Globe,
    description: 'HTTP 调试 / IP·子网 / DNS / UA 解析',
  },
  {
    id: 'image',
    label: '图片处理 · Image',
    shortLabel: '图片',
    icon: ImageIcon,
    description: 'EXIF 查看 / 裁剪 / 单图优化 / 批量压缩，全部纯客户端',
  },
];

export type ToolStatus = 'available' | 'planned';

export interface ToolDef {
  /** Short id, e.g. 'json'. Becomes the last path segment. */
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  category: ToolCategoryId;
  status: ToolStatus;
  /** Always shaped as `/tools/<category>/<id>`. */
  path: string;
  priority: 'P0' | 'P1' | 'P2';
  /**
   * Page component. Required when `status === 'available'`, absent for
   * planned placeholders (CategoryView falls back to `ToolPlaceholder`).
   *
   * Either an eagerly-imported component (current default — keeps the
   * SPA chunk graph tight enough that workbox-build's serializer
   * doesn't race the terser pass on small icon chunks) or a
   * `React.lazy(...)` shell when a future tool grows large enough to
   * warrant code-splitting on its own.
   */
  Component?: ComponentType | LazyExoticComponent<ComponentType>;
}

export const tools: ToolDef[] = [
  {
    id: 'json',
    name: 'JSON 工具',
    description: '格式化 / 压缩 / 校验 / 复制',
    icon: Braces,
    category: 'format',
    status: 'available',
    path: '/tools/format/json',
    priority: 'P0',
    Component: JsonTool,
  },
  {
    id: 'yaml',
    name: 'YAML 工具',
    description: '美化 / 压缩 / YAML ↔ JSON 双向互转',
    icon: FileText,
    category: 'format',
    status: 'available',
    path: '/tools/format/yaml',
    priority: 'P0',
    Component: YamlTool,
  },
  {
    id: 'xml',
    name: 'XML 工具',
    description: '美化 / 压缩 / 闭合校验，含命名空间与 CDATA',
    icon: Tag,
    category: 'format',
    status: 'available',
    path: '/tools/format/xml',
    priority: 'P0',
    Component: XmlTool,
  },
  {
    id: 'sql',
    name: 'SQL 工具',
    description: '多方言美化 / 压缩，关键字大小写可调',
    icon: Database,
    category: 'format',
    status: 'available',
    path: '/tools/format/sql',
    priority: 'P0',
    Component: SqlTool,
  },
  {
    id: 'base64-file',
    name: 'base64 文件互转',
    description: '文件 → Data URI；Data URI / 裸 base64 → 文件下载（10 MB 上限）',
    icon: FileUp,
    category: 'encode',
    status: 'available',
    path: '/tools/encode/base64-file',
    priority: 'P0',
    Component: Base64FileTool,
  },
  {
    id: 'html-entities',
    name: 'HTML 实体编解码',
    description: '编码 / 解码 HTML 实体，支持 minimal / named / numeric 三种模式',
    icon: Code2,
    category: 'encode',
    status: 'available',
    path: '/tools/encode/html-entities',
    priority: 'P0',
    Component: HtmlEntitiesTool,
  },
  {
    id: 'codec',
    name: '编解码',
    description: 'Base64 / URL / Hex 三合一，附 URL 查询参数检查器',
    icon: Code,
    category: 'encode',
    status: 'available',
    path: '/tools/encode/codec',
    priority: 'P0',
    Component: CodecTool,
  },
  {
    id: 'hash',
    name: '哈希计算器',
    description: 'MD5 / SHA-1 / SHA-256 / SHA-384 / SHA-512 多算法并行',
    icon: Hash,
    category: 'crypto',
    status: 'available',
    path: '/tools/crypto/hash',
    priority: 'P0',
    Component: HashTool,
  },
  {
    id: 'hash-file',
    name: '文件摘要',
    description: '文件 MD5 / SHA-1 / SHA-256 / SHA-512，浏览器流式读取（8 MiB 分块）',
    icon: FileLock2,
    category: 'crypto',
    status: 'available',
    path: '/tools/crypto/hash-file',
    priority: 'P0',
    Component: HashFileTool,
  },
  {
    id: 'hmac',
    name: 'HMAC',
    description: 'HMAC-SHA-1 / 256 / 384 / 512，消息+密钥双输入',
    icon: Hash,
    category: 'crypto',
    status: 'available',
    path: '/tools/crypto/hmac',
    priority: 'P0',
    Component: HmacTool,
  },
  {
    id: 'jwt-decode',
    name: 'JWT 解码',
    description: 'header / payload / signature 三段拆解（仅解码不验签）',
    icon: Fingerprint,
    category: 'crypto',
    status: 'available',
    path: '/tools/crypto/jwt-decode',
    priority: 'P0',
    Component: JwtDecodeTool,
  },
  {
    id: 'token',
    name: 'Token 生成',
    description: '密码学随机字节，hex / base64 / base64url',
    icon: KeyRound,
    category: 'crypto',
    status: 'available',
    path: '/tools/crypto/token',
    priority: 'P0',
    Component: TokenTool,
  },
  {
    id: 'uuid',
    name: 'UUID 生成',
    description: 'UUID v1 / v4 / v5 / v7，可批量生成与复制',
    icon: Fingerprint,
    category: 'generate',
    status: 'available',
    path: '/tools/generate/uuid',
    priority: 'P0',
    Component: UuidTool,
  },
  {
    id: 'password',
    name: '密码生成',
    description: 'CSPRNG 拒绝采样，4 字符集可选 + 强度估算',
    icon: Key,
    category: 'generate',
    status: 'available',
    path: '/tools/generate/password',
    priority: 'P0',
    Component: PasswordTool,
  },
  {
    id: 'nanoid',
    name: 'NanoID / ULID',
    description: 'NanoID 短随机 ID + ULID 时间有序 ID（双 tab）',
    icon: Rocket,
    category: 'generate',
    status: 'available',
    path: '/tools/generate/nanoid',
    priority: 'P0',
    Component: NanoIdTool,
  },
  {
    id: 'lorem',
    name: 'Lorem 占位文本',
    description: '拉丁 lorem ipsum / 中文成语短语，词 / 句 / 段三种粒度',
    icon: Pilcrow,
    category: 'generate',
    status: 'available',
    path: '/tools/generate/lorem',
    priority: 'P0',
    Component: LoremTool,
  },
  {
    id: 'qr',
    name: '二维码生成',
    description: '5 种预设：文本 / Wi-Fi / vCard / mailto / sms，PNG/SVG 双下载',
    icon: QrCode,
    category: 'generate',
    status: 'available',
    path: '/tools/generate/qr',
    priority: 'P0',
    Component: QrTool,
  },
  {
    id: 'fake-data',
    name: '假数据生成',
    description: '26 字段（姓名/邮箱/手机号/身份证/地址 等）+ 单字段批量 + 组合记录 JSON/CSV/SQL 导出',
    icon: Users,
    category: 'generate',
    status: 'available',
    path: '/tools/generate/fake-data',
    priority: 'P1',
    Component: FakeDataTool,
  },
  {
    id: 'jsonpath',
    name: 'JSONPath',
    description: 'JSONPath 表达式实时求值，命中节点 + 路径定位',
    icon: Search,
    category: 'validate',
    status: 'available',
    path: '/tools/validate/jsonpath',
    priority: 'P0',
    Component: JsonPathTool,
  },
  {
    id: 'json-schema',
    name: 'JSON Schema 校验',
    description: 'ajv 8 + ajv-formats，2020-12 / 2019-09 / Draft-7/6，远端 $ref 默认禁用',
    icon: ListChecks,
    category: 'validate',
    status: 'available',
    path: '/tools/validate/json-schema',
    priority: 'P0',
    Component: JsonSchemaTool,
  },
  {
    id: 'cron',
    name: 'Cron 预览',
    description: 'cron 表达式人类描述（中英）+ 未来 N 次运行时刻',
    icon: CalendarClock,
    category: 'validate',
    status: 'available',
    path: '/tools/validate/cron',
    priority: 'P0',
    Component: CronTool,
  },
  {
    id: 'regex',
    name: 'Regex Tester',
    description: '正则在线测试，支持替换 / 捕获组高亮 / Worker 防灾难性回溯',
    icon: Regex,
    category: 'validate',
    status: 'available',
    path: '/tools/validate/regex',
    priority: 'P0',
    Component: RegexTool,
  },
  {
    id: 'unix-time',
    name: 'Unix 时间戳',
    description: 'Unix epoch 与 ISO 8601 / 本地时间互转',
    icon: Timer,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/unix-time',
    priority: 'P0',
    Component: UnixTimeTool,
  },
  {
    id: 'number-base',
    name: '进制转换',
    description: '2 / 8 / 10 / 16 / 自定义 2–36，BigInt 精度',
    icon: Binary,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/number-base',
    priority: 'P0',
    Component: NumberBaseTool,
  },
  {
    id: 'json-yaml',
    name: 'JSON ↔ YAML',
    description: '双向互转，js-yaml 复用 anchors / 多行；YAML 注释在 JSON 端会丢失',
    icon: Repeat,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/json-yaml',
    priority: 'P0',
    Component: JsonYamlTool,
  },
  {
    id: 'text-case',
    name: '命名风格转换',
    description: 'camel / pascal / kebab / snake / SCREAM / dot / path / Sentence / Title 一次性 9 种',
    icon: CaseSensitive,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/text-case',
    priority: 'P0',
    Component: TextCaseTool,
  },
  {
    id: 'text-diff',
    name: '文本对比',
    description: '行级 / 字符级 × 并排 / 统一双视图，jsdiff LCS 计算',
    icon: Diff,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/text-diff',
    priority: 'P0',
    Component: TextDiffTool,
  },
  {
    id: 'color',
    name: '颜色转换',
    description: 'HEX/RGB/HSL/HSV/OKLCH/LAB 多格式互转 + 4 种调色板',
    icon: Palette,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/color',
    priority: 'P0',
    Component: ColorTool,
  },
  {
    id: 'text-stats',
    name: '文本统计',
    description: '字符 / 单词 / 行 / 段落 / 句子 / UTF-8 字节',
    icon: ScrollText,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/text-stats',
    priority: 'P0',
    Component: TextStatsTool,
  },
  {
    id: 'ip-subnet',
    name: 'IPv4 子网计算',
    description: 'CIDR ↔ IP+掩码、网络/广播/主机范围、二进制位拆色',
    icon: Network,
    category: 'network',
    status: 'available',
    path: '/tools/network/ip-subnet',
    priority: 'P0',
    Component: IpSubnetTool,
  },
  {
    id: 'url-parse',
    name: 'URL 解析',
    description: 'protocol / host / path / query / hash 结构化拆解',
    icon: Link2,
    category: 'network',
    status: 'available',
    path: '/tools/network/url-parse',
    priority: 'P0',
    Component: UrlParseTool,
  },
  {
    id: 'http-status',
    name: 'HTTP 状态码字典',
    description: '50+ 标准状态码中英文释义、用法说明、RFC 链接',
    icon: ServerCog,
    category: 'network',
    status: 'available',
    path: '/tools/network/http-status',
    priority: 'P0',
    Component: HttpStatusTool,
  },
  {
    id: 'user-agent',
    name: 'User-Agent 解析',
    description: '拆解 UA 字符串：浏览器 / 引擎 / 操作系统 / 设备 / CPU 五段',
    icon: Smartphone,
    category: 'network',
    status: 'available',
    path: '/tools/network/user-agent',
    priority: 'P0',
    Component: UserAgentTool,
  },
  {
    id: 'http',
    name: 'HTTP 调试客户端',
    description: '浏览器内调用接口：方法 / URL / 头 / Body / Auth + 响应面板（CORS 失败给出代理提示）',
    icon: Globe,
    category: 'network',
    status: 'available',
    path: '/tools/network/http',
    priority: 'P0',
    Component: HttpTool,
  },
  /*
   * Image tools (Squoosh-grade pipeline). All four share a common
   * codec pool + WASM workers (jSquash mozjpeg/oxipng/libwebp/avif),
   * with codecs lazy-loaded per chunk:
   *   - 'jsquash-avif' chunk: ~3.3 MB gzip — only loaded when the user
   *     selects AVIF as the output format
   *   - 'jsquash-core' chunk: mozjpeg + oxipng + libwebp + resize,
   *     loaded once by any of the three transform tools
   * Per-tool pages are themselves React.lazy() so the codec graph
   * never lands in the SPA shell.
   *
   * `image-exif-viewer` lives in its own chunk because exifr +
   * piexifjs are unrelated to the codec pool.
   *
   * Filed under status='planned' through commit 1; each subsequent
   * commit flips one to 'available' as its page lands.
   */
  {
    id: 'exif-viewer',
    name: 'EXIF 查看 / 清除',
    description: '查看相机 / GPS / 时间元数据，JPEG 一键下载清除版（不重编码像素）',
    icon: Stamp,
    category: 'image',
    status: 'available',
    path: '/tools/image/exif-viewer',
    priority: 'P0',
    Component: ExifViewerTool,
  },
  {
    id: 'crop',
    name: '图片裁剪',
    description: '8 手柄选区 / 比例锁 / 键盘微调，输出 PNG / JPEG / WebP / AVIF',
    icon: Crop,
    category: 'image',
    status: 'available',
    path: '/tools/image/crop',
    priority: 'P0',
    Component: CropTool,
  },
  {
    id: 'image-optimize',
    name: '图片优化',
    description: '单图全能：编码器（mozjpeg / oxipng / webp / avif）+ 质量 + 尺寸 + EXIF 保留，实时 before/after',
    icon: Wand,
    category: 'image',
    status: 'available',
    path: '/tools/image/image-optimize',
    priority: 'P0',
    Component: ImageOptimizeTool,
  },
  {
    id: 'image-batch',
    name: '批量压缩',
    description: '一次拖入多张：队列 / 并发 / fflate STORE ZIP 一键打包下载',
    icon: Images,
    category: 'image',
    status: 'available',
    path: '/tools/image/image-batch',
    priority: 'P0',
    Component: ImageBatchTool,
  },

  /* ----------------------------------------------------------------
   * P1 placeholders — 35+ tools tracked for the next 5 implementation
   * waves. All entries here are `status: 'planned'` and intentionally
   * have NO `Component`. The router falls through to ToolPlaceholder
   * for these paths (App.tsx wildcard route after the available-tools
   * loop), so cards on `/tools/<category>` stay clickable and URLs
   * remain shareable.
   *
   * Adding a planned entry has zero runtime cost: no chunk load, no
   * route-component graph nodes. Each wave flips entries to
   * `status: 'available'` + Component as their pages land.
   * ---------------------------------------------------------------- */

  /* format · Wave 3 */
  {
    id: 'markdown',
    name: 'Markdown 编辑',
    description: 'unified + remark-gfm + remark-math + KaTeX 实时双栏预览，含一键 remark-stringify 规范化',
    icon: BookText,
    category: 'format',
    status: 'available',
    path: '/tools/format/markdown',
    priority: 'P1',
    Component: MarkdownTool,
  },
  {
    id: 'html',
    name: 'HTML 美化',
    description: 'prettier standalone + parser-html 实时格式化，可调 printWidth / tabWidth',
    icon: FileCode2,
    category: 'format',
    status: 'available',
    path: '/tools/format/html',
    priority: 'P1',
    Component: HtmlFormatTool,
  },
  {
    id: 'css',
    name: 'CSS 美化',
    description: 'prettier standalone + parser-postcss，支持 CSS / SCSS / Less，可调 printWidth',
    icon: Paintbrush,
    category: 'format',
    status: 'available',
    path: '/tools/format/css',
    priority: 'P1',
    Component: CssFormatTool,
  },
  {
    id: 'svg',
    name: 'SVG 优化',
    description: 'svgo 多重优化 + 实时缩略图对比，输入 / 输出字节数 + 节省比例展示',
    icon: PenTool,
    category: 'format',
    status: 'available',
    path: '/tools/format/svg',
    priority: 'P1',
    Component: SvgFormatTool,
  },

  /* encode · Wave 1 (unicode/binary/gzip) + Wave 4 (punycode) */
  {
    id: 'unicode',
    name: 'Unicode 转义',
    description: 'JS 字面量 \\uXXXX / HTML 十进制 / HTML 十六进制 / Percent 四模式互转',
    icon: Languages,
    category: 'encode',
    status: 'available',
    path: '/tools/encode/unicode',
    priority: 'P1',
    Component: UnicodeTool,
  },
  {
    id: 'punycode',
    name: 'Punycode',
    description: 'IDN 国际化域名 ↔ ASCII (xn--…) 互转，0 dep RFC 3492 实现，按 label 编码',
    icon: Globe2,
    category: 'encode',
    status: 'available',
    path: '/tools/encode/punycode',
    priority: 'P1',
    Component: PunycodeTool,
  },
  {
    id: 'binary',
    name: '二进制查看',
    description: '十六进制 dump（offset / hex / ASCII 三栏）+ 反向解析回字节流',
    icon: FileDigit,
    category: 'encode',
    status: 'available',
    path: '/tools/encode/binary',
    priority: 'P1',
    Component: BinaryTool,
  },
  {
    id: 'gzip',
    name: 'gzip 压缩',
    description: '浏览器原生 CompressionStream，文本 ↔ base64-gzip 双向 + 压缩比展示',
    icon: FileArchive,
    category: 'encode',
    status: 'available',
    path: '/tools/encode/gzip',
    priority: 'P1',
    Component: GzipTool,
  },

  /* crypto · Wave 1 (aes) + Wave 2 (rsa-keygen / rsa / bcrypt / jwt-sign) */
  {
    id: 'aes',
    name: 'AES 加解密',
    description: 'AES-256-GCM + PBKDF2-SHA-256 256k 轮派生密钥，浏览器内运算不上传',
    icon: KeySquare,
    category: 'crypto',
    status: 'available',
    path: '/tools/crypto/aes',
    priority: 'P1',
    Component: AesTool,
  },
  {
    id: 'rsa-keygen',
    name: 'RSA 密钥对',
    description: 'SubtleCrypto 生成 OAEP / PSS / PKCS1 2048-4096 位密钥对，PKCS#8 + SPKI PEM 输出',
    icon: FileKey,
    category: 'crypto',
    status: 'available',
    path: '/tools/crypto/rsa-keygen',
    priority: 'P1',
    Component: RsaKeygenTool,
  },
  {
    id: 'rsa',
    name: 'RSA 加解密',
    description: 'SubtleCrypto RSA-OAEP 加解密 + PSS / PKCS1 签名验签，配合 rsa-keygen 使用',
    icon: FileKey2,
    category: 'crypto',
    status: 'available',
    path: '/tools/crypto/rsa',
    priority: 'P1',
    Component: RsaTool,
  },
  {
    id: 'bcrypt',
    name: 'bcrypt 哈希',
    description: 'bcrypt 密码哈希与校验，cost 4-15 可调，bcryptjs lazy chunk',
    icon: Shield,
    category: 'crypto',
    status: 'available',
    path: '/tools/crypto/bcrypt',
    priority: 'P1',
    Component: BcryptTool,
  },
  {
    id: 'jwt-sign',
    name: 'JWT 签发',
    description: 'HS256/384/512 + RS256/384/512 签发 / 验签，配合 rsa-keygen 私钥使用',
    icon: PenLine,
    category: 'crypto',
    status: 'available',
    path: '/tools/crypto/jwt-sign',
    priority: 'P1',
    Component: JwtSignTool,
  },

  /* generate · Wave 2 */
  {
    id: 'cron-builder',
    name: 'Cron 构建器',
    description: '5 段可视化构建（每段 5 模式）+ 实时人类描述 + 未来运行时刻预览，与 validate/cron 共 lib',
    icon: Calendar,
    category: 'generate',
    status: 'available',
    path: '/tools/generate/cron-builder',
    priority: 'P1',
    Component: CronBuilderTool,
  },
  {
    id: 'slug',
    name: 'Slug 生成',
    description: '任意文本 → URL slug，CJK 走 pinyin-pro 转拼音（lazy chunk），可调分隔符 / 大小写 / 长度上限',
    icon: AtSign,
    category: 'generate',
    status: 'available',
    path: '/tools/generate/slug',
    priority: 'P1',
    Component: SlugTool,
  },
  {
    id: 'mock-json',
    name: 'Mock JSON',
    description: 'JSON Schema → 假数据，可固定 seed 复现，支持 format（email / uuid / date-time …），json-schema-faker lazy chunk',
    icon: FlaskConical,
    category: 'generate',
    status: 'available',
    path: '/tools/generate/mock-json',
    priority: 'P1',
    Component: MockJsonTool,
  },
  {
    id: 'color-palette',
    name: '调色板生成',
    description: '种子色一键生成 Tailwind / Material 风格 50–950 色阶，OKLCH 感知亮度曲线，复用 culori chunk',
    icon: SwatchBook,
    category: 'generate',
    status: 'available',
    path: '/tools/generate/color-palette',
    priority: 'P1',
    Component: ColorPaletteTool,
  },
  {
    id: 'qr-reader',
    name: '二维码识别',
    description: '拖入 / 粘贴图片解码二维码，自动识别 URL / Wi-Fi / vCard / mailto 等载荷类型，jsqr lazy chunk',
    icon: ScanLine,
    category: 'generate',
    status: 'available',
    path: '/tools/generate/qr-reader',
    priority: 'P1',
    Component: QrReaderTool,
  },

  /* validate · Wave 1 (email/url/ip) + Wave 2 (phone) */
  {
    id: 'email',
    name: '邮箱校验',
    description: 'RFC 5321/5322 简化正则 + 拆分 local/domain，浏览器不做 DNS / SMTP 探活',
    icon: Mail,
    category: 'validate',
    status: 'available',
    path: '/tools/validate/email',
    priority: 'P1',
    Component: EmailTool,
  },
  {
    id: 'phone',
    name: '电话号码校验',
    description: 'libphonenumber-js/min 解析号码：国家 / 类型 / 国际+E.164+RFC 3966 多格式输出',
    icon: Phone,
    category: 'validate',
    status: 'available',
    path: '/tools/validate/phone',
    priority: 'P1',
    Component: PhoneTool,
  },
  {
    id: 'ip-validate',
    name: 'IP 校验',
    description: 'IPv4 / IPv6 / CIDR 字符串校验 + 私网/回环/链路本地/组播/保留分类',
    icon: Cable,
    category: 'validate',
    status: 'available',
    path: '/tools/validate/ip-validate',
    priority: 'P1',
    Component: IpValidateTool,
  },
  {
    id: 'url-validate',
    name: 'URL 校验',
    description: 'URL 构造器 + 字段拆分 + 协议白名单，定位非法的具体原因',
    icon: Link,
    category: 'validate',
    status: 'available',
    path: '/tools/validate/url-validate',
    priority: 'P1',
    Component: UrlValidateTool,
  },

  /* convert · Wave 1 (sort-lines / dedupe-lines / unit) + Wave 2/3 rest */
  {
    id: 'json-csv',
    name: 'JSON ↔ CSV',
    description: 'papaparse RFC 4180 双向互转，可调分隔符 / 表头开关，papaparse lazy chunk',
    icon: Sheet,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/json-csv',
    priority: 'P1',
    Component: JsonCsvTool,
  },
  {
    id: 'json-toml',
    name: 'JSON ↔ TOML',
    description: 'smol-toml 双向互转 (TOML 1.0)，~5 KB gzip 懒加载，全部在浏览器内完成',
    icon: FileCog,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/json-toml',
    priority: 'P1',
    Component: JsonTomlTool,
  },
  {
    id: 'json-xml',
    name: 'JSON ↔ XML',
    description: '复用 fast-xml-parser 双向互转，属性使用 @_ 前缀映射',
    icon: Brackets,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/json-xml',
    priority: 'P1',
    Component: JsonXmlTool,
  },
  {
    id: 'markdown-html',
    name: 'Markdown ↔ HTML',
    description: '复用 format/markdown 的 unified pipeline 双向互转，GFM 默认开启',
    icon: FileType,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/markdown-html',
    priority: 'P1',
    Component: MarkdownHtmlTool,
  },
  {
    id: 'unit',
    name: '单位换算',
    description: '长度 / 重量 / 温度 / 数据 / 时间 五维换算，输入即所有单位实时显示',
    icon: Scale,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/unit',
    priority: 'P1',
    Component: UnitTool,
  },
  {
    id: 'sort-lines',
    name: '行排序',
    description: '升 / 降 / 长度 / 自然（Intl.Collator numeric） / 反转 6 模式 + 大小写选项',
    icon: ArrowDownAZ,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/sort-lines',
    priority: 'P1',
    Component: SortLinesTool,
  },
  {
    id: 'dedupe-lines',
    name: '行去重',
    description: '保留首次 / 末次出现 + 计数模式 + 大小写不敏感 + 自动 trim',
    icon: ListFilter,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/dedupe-lines',
    priority: 'P1',
    Component: DedupeLinesTool,
  },
  {
    id: 'image-to-pdf',
    name: '图片转 PDF',
    description:
      '把若干图片合并成一份 PDF：A4 / Letter 等纸张 + 纵横朝向 + 每页 1/2/4 张拼版 + 边距与适应模式。纯前端，jspdf 仅在首次进入本工具时按需加载。',
    icon: FileImage,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/image-to-pdf',
    priority: 'P1',
    Component: ImageToPdfTool,
  },
  {
    id: 'pdf-merge',
    name: 'PDF 合并',
    description:
      '按你给的顺序把多个 PDF 拼接成一份。pdf-lib copyPages 复制资源，避免共享字体 / 图片导致的引用错乱。纯前端，单个文件 ≤ 50 MB。',
    icon: Combine,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/pdf-merge',
    priority: 'P1',
    Component: PdfMergeTool,
  },
  {
    id: 'pdf-split',
    name: 'PDF 拆分',
    description:
      '把 PDF 拆成多个文件并打成 ZIP：按范围语法（1-3, 5, 7-9）/ 每页一个文件 / 每 N 页一个文件 三模式。纯前端，复用 fflate STORE 模式打包。',
    icon: Scissors,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/pdf-split',
    priority: 'P1',
    Component: PdfSplitTool,
  },
  {
    id: 'pdf-watermark',
    name: 'PDF 加水印',
    description:
      '给 PDF 加文字 / 图片水印：9 宫格位置或全页平铺 + 字号 / 颜色 / 不透明度 / 旋转角度 + 指定页范围。预览用 canvas 模拟 A4 排版，最终效果以下载 PDF 为准。',
    icon: Stamp,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/pdf-watermark',
    priority: 'P1',
    Component: PdfWatermarkTool,
  },
  {
    id: 'pdf-rotate',
    name: 'PDF 旋转',
    description:
      '选定页码 + 旋转角度，原地改写 PDF 的 /Rotate 属性。支持全部 / 奇数 / 偶数 / 自定义范围 + ±90° / 180°。纯前端，所有处理本地完成。',
    icon: RotateCw,
    category: 'convert',
    status: 'available',
    path: '/tools/convert/pdf-rotate',
    priority: 'P1',
    Component: PdfRotateTool,
  },

  /* network · Wave 1 (curl-convert) + Wave 4 (ipv6 / cookie / websocket / mac-lookup) + Wave 5 (helper-backed) */
  {
    id: 'ipv6-tools',
    name: 'IPv6 工具',
    description: 'IPv6 完整 ↔ 缩写 (RFC 5952) + RFC 4193 ULA /48 一键生成，纯 JS 0 dep',
    icon: Wifi,
    category: 'network',
    status: 'available',
    path: '/tools/network/ipv6-tools',
    priority: 'P1',
    Component: Ipv6ToolsTool,
  },
  {
    id: 'dns',
    name: 'DNS 查询',
    description:
      '走本机 helper /api/net/dns 查 A / AAAA / MX / TXT / SRV / NS / CNAME / PTR 八种记录类型，复用电脑 OS DNS 配置；本会话历史 10 条。',
    icon: Server,
    category: 'network',
    status: 'available',
    path: '/tools/network/dns',
    priority: 'P1',
    Component: DnsTool,
  },
  {
    id: 'whois',
    name: 'WHOIS 查询',
    description:
      '走本机 helper /api/net/whois 查域名 / IP 注册信息：优先调本机 whois cli，找不到时回落公共 RDAP 网关；解析后展示 registrar / 创建 / 到期 / NS 等关键字段，原始响应可折叠展开。',
    icon: UserSearch,
    category: 'network',
    status: 'available',
    path: '/tools/network/whois',
    priority: 'P1',
    Component: WhoisTool,
  },
  {
    id: 'mac-lookup',
    name: 'MAC 厂商查询',
    description: 'MAC 解析 + OUI 厂商查询（精选约 200 家），含 U/L 与多播位识别，OUI 表 lazy chunk',
    icon: Cpu,
    category: 'network',
    status: 'available',
    path: '/tools/network/mac-lookup',
    priority: 'P1',
    Component: MacLookupTool,
  },
  {
    id: 'websocket',
    name: 'WebSocket 调试',
    description: '浏览器原生 WebSocket 客户端：连接 / 发送 / 接收 / 断开 + 时间戳日志，0 dep',
    icon: Zap,
    category: 'network',
    status: 'available',
    path: '/tools/network/websocket',
    priority: 'P1',
    Component: WebSocketTool,
  },
  {
    id: 'curl-convert',
    name: 'cURL 互转',
    description: 'cURL ↔ fetch / axios / Python requests / wget 多语言互转',
    icon: Terminal,
    category: 'network',
    status: 'available',
    path: '/tools/network/curl-convert',
    priority: 'P1',
    Component: CurlConvertTool,
  },
  {
    id: 'og-preview',
    name: 'OG 预览',
    description:
      '走本机 helper /api/net/og 抓页面 head 并提取 og:* / twitter:* / description / favicon。带三层 SSRF 防护（lexical + DNS + 重定向后再查），渲染分享卡 + meta 表 + 折叠原始 head。',
    icon: Eye,
    category: 'network',
    status: 'available',
    path: '/tools/network/og-preview',
    priority: 'P1',
    Component: OgPreviewTool,
  },
  {
    id: 'cookie-decoder',
    name: 'Cookie 解析',
    description: 'Cookie / Set-Cookie 字段拆解：name / value (URL-decoded) / Path / Domain / Expires / SameSite，0 dep',
    icon: Cookie,
    category: 'network',
    status: 'available',
    path: '/tools/network/cookie-decoder',
    priority: 'P1',
    Component: CookieDecoderTool,
  },

  /* image · Wave 2/4 */
  {
    id: 'color-extract',
    name: '主色提取',
    description: 'canvas 采样 + k-means 聚类，输出 3/5/8/12 主色调色板，0 dep（懒加载页面）',
    icon: Pipette,
    category: 'image',
    status: 'available',
    path: '/tools/image/color-extract',
    priority: 'P1',
    Component: ColorExtractTool,
  },
  {
    id: 'placeholder-svg',
    name: 'SVG 占位图',
    description: '0 dep 占位图：尺寸 / 文本 / 配色 / 边框，输出 SVG 与 data: URL，无需 placehold.co',
    icon: Frame,
    category: 'image',
    status: 'available',
    path: '/tools/image/placeholder-svg',
    priority: 'P1',
    Component: PlaceholderSvgTool,
  },
];

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

export function getCategory(id: string | undefined): CategoryDef | undefined {
  if (!id) return undefined;
  return categories.find((c) => c.id === id);
}

export function getAvailableTools(): ToolDef[] {
  return tools.filter((t) => t.status === 'available');
}

export function getToolsByCategory(c: ToolCategoryId): ToolDef[] {
  return tools.filter((t) => t.category === c);
}

export function getToolByPath(path: string): ToolDef | undefined {
  return tools.find((t) => t.path === path);
}

export function getToolByIds(category: string, toolId: string): ToolDef | undefined {
  return tools.find((t) => t.category === category && t.id === toolId);
}

/**
 * Stable union of valid category ids — exported so callers like
 * `App.tsx` can build typed route guards without re-listing the enum.
 */
export const CATEGORY_IDS: readonly ToolCategoryId[] = categories.map((c) => c.id);
